import type { Styles } from '@adornis/ass/style.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/chemistry/elements/components/x-button';
import '@adornis/chemistry/elements/components/x-flex';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { shareOnSocial, type SocialShareOptions, type SocialShareType } from '../social-share';

@customElement('bh-share')
export class Share extends BuchungsheldLitElement {
  /**
   * Hide the text and only show the icons
   */
  @property({ attribute: 'only-icons', type: Boolean }) onlyIcons = false;
  @property({ attribute: 'icon-color' }) iconColor: string | undefined;
  @property({ type: String }) iconSize = '0.9em';

  @property({ attribute: false }) shareTypes: SocialShareType[] = ['facebook', 'whatsapp', 'linkedin', 'twitter'];

  @property({ attribute: false }) shareOptions!: SocialShareOptions;

  protected getFontColor(defaultColor: string) {
    return this.iconColor ?? defaultColor;
  }

  override render() {
    const getFC = (s: string) => this.getFontColor(s);

    return html`
      <x-flex content horizontal space="xs">
        ${this.shareTypes.map(shareType => {
          switch (shareType.toLowerCase()) {
            case 'facebook':
              return this._renderFacebookButton(getFC('#1877F2'));
            case 'whatsapp':
              return this._renderWhatsAppButton(getFC('#25D366'));
            case 'linkedin':
              return this._renderLinkedInButton(getFC('#0A66C2'));
            case 'twitter':
            //return this._renderXButton(getFC('#1DA1F2'));
            case 'x':
              return this._renderXButton(getFC('#000000'));
            default:
              return html``;
          }
        })}
      </x-flex>
    `;
  }

  _renderFacebookButton(color: string) {
    return html`
      <x-button mode="text" flex @click=${() => shareOnSocial('facebook', this.shareOptions)}>
        <x-flex horizontal space="xxs">
          <svg xmlns="http://www.w3.org/2000/svg" width=${this.iconSize} height=${this.iconSize} viewBox="0 0 24 24">
            <path
              fill="${color}"
              d="M12 2.04c-5.5 0-10 4.49-10 10.02c0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89c1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02"
            />
          </svg>
          ${this.onlyIcons ? '' : html` <x-text> ${this._translation.translate('share', 'facebook')} </x-text> `}
        </x-flex>
      </x-button>
    `;
  }

  _renderWhatsAppButton(color: string) {
    return html`
      <x-button mode="text" flex @click=${() => shareOnSocial('whatsapp', this.shareOptions)}>
        <x-flex horizontal space="xxs">
          <svg xmlns="http://www.w3.org/2000/svg" width=${this.iconSize} height=${this.iconSize} viewBox="0 0 24 24">
            <path
              fill="${color}"
              d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91c0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.23 8.23 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23c-1.48 0-2.93-.39-4.19-1.15l-.3-.17l-3.12.82l.83-3.04l-.2-.32a8.2 8.2 0 0 1-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24M8.53 7.33c-.16 0-.43.06-.66.31c-.22.25-.87.86-.87 2.07c0 1.22.89 2.39 1 2.56c.14.17 1.76 2.67 4.25 3.73c.59.27 1.05.42 1.41.53c.59.19 1.13.16 1.56.1c.48-.07 1.46-.6 1.67-1.18s.21-1.07.15-1.18c-.07-.1-.23-.16-.48-.27c-.25-.14-1.47-.74-1.69-.82c-.23-.08-.37-.12-.56.12c-.16.25-.64.81-.78.97c-.15.17-.29.19-.53.07c-.26-.13-1.06-.39-2-1.23c-.74-.66-1.23-1.47-1.38-1.72c-.12-.24-.01-.39.11-.5c.11-.11.27-.29.37-.44c.13-.14.17-.25.25-.41c.08-.17.04-.31-.02-.43c-.06-.11-.56-1.35-.77-1.84c-.2-.48-.4-.42-.56-.43c-.14 0-.3-.01-.47-.01"
            />
          </svg>
          ${this.onlyIcons ? '' : html` <x-text> ${this._translation.translate('share', 'whatsapp')} </x-text> `}
        </x-flex>
      </x-button>
    `;
  }

  _renderLinkedInButton(color: string) {
    return html`
      <x-button mode="text" flex @click=${() => shareOnSocial('linkedin', this.shareOptions)}>
        <x-flex horizontal space="xxs">
          <svg xmlns="http://www.w3.org/2000/svg" width=${this.iconSize} height=${this.iconSize} viewBox="0 0 24 24">
            <path
              fill="${color}"
              d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93zM6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37z"
            />
          </svg>
          ${this.onlyIcons ? '' : html` <x-text> ${this._translation.translate('share', 'linkedin')} </x-text> `}
        </x-flex>
      </x-button>
    `;
  }

  _renderXButton(color: string) {
    return html`
      <x-button mode="text" flex @click="${() => shareOnSocial('twitter', this.shareOptions)}">
        <x-flex horizontal space="xxs">
          <svg xmlns="http://www.w3.org/2000/svg" width=${this.iconSize} height=${this.iconSize} viewBox="-2 -2 18 18">
            <g fill="none">
              <g clip-path="url(#primeTwitter0)">
                <path
                  fill="${color}"
                  d="M11.025.656h2.147L8.482 6.03L14 13.344H9.68L6.294 8.909l-3.87 4.435H.275l5.016-5.75L0 .657h4.43L7.486 4.71zm-.755 11.4h1.19L3.78 1.877H2.504z"
                />
              </g>
              <defs>
                <clipPath id="primeTwitter0"><path fill="#fff" d="M0 0h14v14H0z" /></clipPath>
              </defs>
            </g>
          </svg>
          ${this.onlyIcons ? '' : html` <x-text> ${this._translation.translate('share', 'twitter')} </x-text> `}
        </x-flex>
      </x-button>
    `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '[content] > *': {
          padding: this.spacing.xs,
          aspectRatio: this.onlyIcons ? '1 / 1' : 'unset',
        },
      },
    ];
  }
}
